import React, { useState } from 'react';
import { motion } from 'framer-motion';

const quizQuestions = [
  {
    id: 1,
    question: "Who is known as the divine charioteer in the Mahabharata?",
    options: ["Rama", "Krishna", "Arjuna", "Bhishma"],
    correctAnswer: 1,
    explanation: "Krishna served as Arjuna's charioteer during the Kurukshetra war and delivered the Bhagavad Gita."
  },
  {
    id: 2,
    question: "Which avatar of Vishnu is depicted as a fish?",
    options: ["Kurma", "Matsya", "Varaha", "Narasimha"],
    correctAnswer: 1,
    explanation: "Matsya is the fish avatar of Vishnu who saved the world from a great deluge."
  },
  {
    id: 3,
    question: "Who wrote the epic Ramayana?",
    options: ["Vyasa", "Valmiki", "Tulsidas", "Kalidasa"],
    correctAnswer: 1,
    explanation: "The sage Valmiki composed the epic Ramayana, which tells the story of Lord Rama."
  },
  {
    id: 4,
    question: "What is the name of Krishna's beloved consort?",
    options: ["Sita", "Draupadi", "Radha", "Kunti"],
    correctAnswer: 2,
    explanation: "Radha is considered Krishna's eternal beloved and divine consort."
  },
  {
    id: 5,
    question: "How many heads does Lord Brahma have?",
    options: ["Three", "Four", "Five", "One"],
    correctAnswer: 1,
    explanation: "Lord Brahma is depicted with four heads representing the four Vedas."
  }
];

const MythologyQuiz = () => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [score, setScore] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [quizCompleted, setQuizCompleted] = useState(false);

  const handleOptionSelect = (optionIndex) => {
    setSelectedOption(optionIndex);
    
    // Check if answer is correct
    if (optionIndex === quizQuestions[currentQuestion].correctAnswer) {
      setScore(score + 1);
    }
    
    // Move to next question or show results
    setTimeout(() => {
      if (currentQuestion < quizQuestions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
        setSelectedOption(null);
      } else {
        setQuizCompleted(true);
      }
      setShowResult(true);
    }, 1000);
  };

  const restartQuiz = () => {
    setCurrentQuestion(0);
    setSelectedOption(null);
    setScore(0);
    setShowResult(false);
    setQuizCompleted(false);
  };

  const getOptionStyle = (index) => {
    if (selectedOption === null) {
      return "bg-white hover:bg-yellow-50 border-yellow-300";
    }
    
    if (index === quizQuestions[currentQuestion].correctAnswer) {
      return "bg-green-100 border-green-500";
    }
    
    if (index === selectedOption && index !== quizQuestions[currentQuestion].correctAnswer) {
      return "bg-red-100 border-red-500";
    }
    
    return "bg-gray-50 border-gray-300";
  };

  return (
    <div className="max-w-3xl mx-auto">
      {!quizCompleted ? (
        <div className="bg-gradient-to-br from-red-50 to-yellow-50 rounded-2xl p-6 md:p-8 shadow-lg">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-red-600">Mythology Quiz</h2>
            <div className="bg-yellow-100 px-4 py-2 rounded-full">
              <span className="font-bold text-red-600">
                Question {currentQuestion + 1} of {quizQuestions.length}
              </span>
            </div>
          </div>
          
          <div className="mb-2">
            <div className="w-full bg-gray-200 rounded-full h-4">
              <motion.div 
                className="bg-red-600 h-4 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${((currentQuestion + 1) / quizQuestions.length) * 100}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 mb-6 shadow-md">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">
              {quizQuestions[currentQuestion].question}
            </h3>
            
            <div className="space-y-3">
              {quizQuestions[currentQuestion].options.map((option, index) => (
                <motion.button
                  key={index}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-300 ${getOptionStyle(index)}`}
                  onClick={() => !selectedOption && handleOptionSelect(index)}
                  disabled={selectedOption !== null}
                >
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center mr-3">
                      <span className="font-bold text-red-600">{String.fromCharCode(65 + index)}</span>
                    </div>
                    <span className="font-medium">{option}</span>
                  </div>
                </motion.button>
              ))}
            </div>
          </div>
          
          {showResult && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl p-6 mb-6 shadow-md"
            >
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                {selectedOption === quizQuestions[currentQuestion].correctAnswer 
                  ? "Correct! 🎉" 
                  : "Incorrect! 😕"}
              </h3>
              <p className="text-gray-600">
                {quizQuestions[currentQuestion].explanation}
              </p>
            </motion.div>
          )}
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-gradient-to-br from-red-50 to-yellow-100 rounded-2xl p-8 text-center shadow-lg"
        >
          <h2 className="text-3xl font-bold text-red-600 mb-4">Quiz Completed!</h2>
          <div className="text-5xl font-bold mb-4 text-yellow-500">
            {score}/{quizQuestions.length}
          </div>
          <p className="text-xl text-gray-700 mb-2">
            {score === quizQuestions.length 
              ? "Perfect! You're a mythology master! 🏆" 
              : score >= quizQuestions.length / 2 
                ? "Great job! You know your mythology well! 👍" 
                : "Keep learning! You'll get better! 💪"}
          </p>
          <p className="text-gray-600 mb-8">
            You scored {Math.round((score / quizQuestions.length) * 100)}%
          </p>
          
          <button
            onClick={restartQuiz}
            className="bg-red-600 text-white font-bold py-3 px-8 rounded-full hover:bg-red-700 transition-colors shadow-lg"
          >
            Play Again
          </button>
        </motion.div>
      )}
    </div>
  );
};

export default MythologyQuiz;